<template>
  <vee-form v-slot="{ handleSubmit }" as="div" class="form-wrap" :onInvalidSubmit="onInvalidSubmit">
    <form @submit.prevent.stop="handleSubmit(doSave)" @keydown.enter="$event.preventDefault()">
      <div class="offcanvas-body">
        <div class="scroll-area scrollbar">
          <p>{{ $t('exam.fillForm') }}</p>

          <PatientFormData
            ref="patientFormData"
            :patient="patient"
            :rightDiopter="rightDiopter"
            :leftDiopter="leftDiopter"
            :underlying="underlying"
            :ophthalmic="ophthalmic"
            :underlyingChange="underlyingChange"
            :ophthalmicChange="ophthalmicChange"
            :groupedLayout="true"/>
        </div>
      </div>

      <div class="offcanvas-footer" v-if="hasFormChanged">
        <button type="submit" name="button" class="btn btn-success" data-test="btnAdd">{{ $t('assignPatient.createPatient') }}</button>
      </div>
    </form>
  </vee-form>
</template>

<style lang="scss" scoped>
.form-wrap {
  display:                            flex;
  flex-direction:                     column;
  flex:                               1;
  min-height:                         0;
  width:                              100%;

  .offcanvas-body {
    display:                          flex;
    flex-direction:                   column;
    flex:                             1;
    min-height:                       0;
    padding:                          0 40px 20px 40px;
    overflow:                         hidden;
  }
}

form {
  display:                            flex;
  flex-direction:                     column;
  width:                              100%;
  flex:                               1;
  min-height:                         0;
}

.scroll-area {
  overflow-y:                         auto;
  padding-right:                      1rem;
  min-height:                         0;
}

.offcanvas-footer {
  @include                            border_radius(10px 10px 0 0);
  @include                            box_shadow(0 0 10px 0 rgba(0, 0, 0, 0.25));
  position:                           relative;
  display:                            flex;
  flex-direction:                     column;
  gap:                                15px;
  padding:                            20px 40px;
  z-index:                            4;
  isolation:                          isolate;

  .btn {
    font-size:                        var(--font-size-14);
    font-weight:                      700;
    line-height:                      normal;
    letter-spacing:                   4.2px;
    text-align:                       center;
    text-transform:                   uppercase;
    width:                            100%;
    margin:                           0;
    padding:                          20px;
    color:                            var(--Shades-W, #FFF);
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import PatientFormData from '../patient/PatientFormData.vue'

export default {
  name: 'ExamPatientAddFormData',
  components: {
    PatientFormData
  },
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    session: {
      collaborator: {},
      currentClinic: {}
    },
    patient: {
      id: null,
      firstName: '',
      lastName: '',
      mrn: '',
      birthday: '',
      birthdayAux: '',
      document0: '',
      document1: '',
      document2: '',
      document3: '',
      document4: '',
      mothersName: '',
      gender: 'M',
      weight: '',
      height: '',
      telephone1: '',
      telephone2: '',
      email: '',
      address: '',
      addressNumber: '',
      anamnesis: {
        otherDisease: ''
      },
      district: '',
      postalCode: '',
      city: '',
      state: '',
      country: '',
      other: '',
    },
    rightDiopter: {
      spherical: '',
      cylindrical: '',
      axis: '',
    },
    leftDiopter: {
      spherical: '',
      cylindrical: '',
      axis: '',
    },
    underlying: {
      diabetes: false,
      hypertension: false,
      cholesterol: false,
      smoker: false,
      nwn_underlying: false,
    },
    ophthalmic: {
      diabeticRetinopathy: false,
      dmri: false,
      glaucoma: false,
      cataract: false,
      pterygium: false,
      lowVisualAcuity: false,
      nwn_ophthalmic: false,
    },
    underlyingChange: true,
    ophthalmicChange: true,
    mandatoryDocument: null,
    hasFormChanged: false,
    initialForm: {},
    changedFormItems: [],
  }),
  watch: {
    patient: {
      immediate: true,
      handler(newValue) {
        this.checkFormChange(newValue, 'patient')
    }, deep: true},
    rightDiopter: {
      immediate: true,
      handler(newRightDiopter) {
        this.checkFormChange(newRightDiopter, 'rightDiopter')
    }, deep: true},
    leftDiopter: {
      immediate: true,
      handler(newLeftDiopter) {
        this.checkFormChange(newLeftDiopter, 'leftDiopter')
    }, deep: true},
    underlying: {
      immediate: true,
      handler(newUnderlying) {
        this.checkFormChange(newUnderlying, 'underlying')
    }, deep: true},
    ophthalmic: {
      immediate: true,
      handler(newOphthalmic) {
        this.checkFormChange(newOphthalmic, 'ophthalmic')
    }, deep: true},
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    this.mandatoryDocument = this.session.currentClinic.mandatoryDocument

    const formWrap = {
      patient: this.patient,
      rightDiopter: this.rightDiopter,
      leftDiopter: this.leftDiopter,
      underlying: this.underlying,
      ophthalmic: this.ophthalmic
    }

    this.initialForm = _.cloneDeep(formWrap)

    this.hasFormChanged = false
    this.changedFormItems = []
  },
  methods: {
    onInvalidSubmit({ errors }) {
      this.$refs.patientFormData?.revealFirstInvalidField(Object.keys(errors))
    },
    checkFormChange(value, obj) {
      let hasObj = this.changedFormItems.includes(obj)
      if (JSON.stringify(value) === JSON.stringify(this.initialForm[obj])) {
        if (hasObj) {
          let index = this.changedFormItems.indexOf(obj)
          this.changedFormItems.splice(index, 1)
        }

        if (this.changedFormItems.length > 0)
          return

        this.hasFormChanged = false
      } else {
        if (!hasObj)
          this.changedFormItems.push(obj)

        this.hasFormChanged = true
      }
    },
    async doSave() {
      try {
        this.alert.message = null;
        // TODO FIX check empty mandatory document on clinic configuration (match Eyer attribute)
        const mandatoryDocumentWasSet = !_.isEmpty(this.mandatoryDocument) && this.mandatoryDocument != 'none'
        if (mandatoryDocumentWasSet) {
          if (_.isEmpty(this.patient[this.mandatoryDocument])) {
            this.$refs.patientFormData?.revealFirstInvalidField([this.mandatoryDocument])
            return
          }
        }

        this.patient.birthday = this.patient.birthdayAux

        let anamnesis = {}

        if (this.patient.anamnesis?.otherDisease)
          anamnesis.otherDisease = this.patient.anamnesis.otherDisease

        Object.assign(anamnesis, this.underlying)
        Object.assign(anamnesis, this.ophthalmic)

        let params = {
          patient: this.patient,
          anamnesis,
          rightDiopter: this.rightDiopter,
          leftDiopter: this.leftDiopter,
        }

        this.loading = true
        NProgress.start()
        let response = await this.$patientService.save(params)

        this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success')

        this.$emit('patient-created', response.result)
      } catch(err) {
        this.$message.popup(this, 'general.createdError', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'general.createdError', 'danger')
      }
      NProgress.done(true)
      this.loading = false
    },
  }
}
</script>
